<template>
    <div class="lp-wave-content">
      <div class="lp-wave-login ly-row ly-center-center" v-load-fade-in="{ delay: 1000, timeBetween: 500 }">
        <transition name="fade">
          <div v-show="true" class="ly-center-center reset-password-container ly-col forgot-password-container lp-load-fadein">
            <div class="card" v-if="showSuccessView">
              <ResetPasswordSuccessView @loginSubmitted="onAutoLoginTriggered"></ResetPasswordSuccessView>
            </div>
            <div class="card" v-else>
              <h1 class="text-center">Set New Password</h1>
              <div class="msg">{{text}}</div>

              <form @submit.prevent="formSubmitted" class="form-reset-password">
                <lp-input v-model="oldPassword" type="password" label-top="Old password" />
                <lp-input v-model="newPassword" type="password" label-top="New password" />
                <lp-input v-model="repeatPassword" type="password" label-top="Confirm password" />
                <PasswordPoliciesList :isSymbolsPolicyMeet="isSymbolsPolicyMeet" :isLengthPolicyMeet="isLengthPolicyMeet"
                  :isLowercasePolicyMeet="isLowercasePolicyMeet" :isRequireNumberPolicyMeet="isRequireNumberPolicyMeet"
                  :isUppercasePolicyMeet="isUppercasePolicyMeet"></PasswordPoliciesList>
                <div class="ly-row mt-2">
                  <lp-button :disabled="disableSubmitButton" type="primary">Submit</lp-button>
                </div>
              </form>

              <div class="msg msg-error w-100 text-center" v-if="errorMessage">
                {{ errorMessage }}
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
</template>

<script setup lang="ts">

import { useConfigSync } from '@/config/useConfig'
import { LMS } from '@/consts/serviceNames'
import { redirect } from '@/services/redirect'
import { ServiceNames } from '@/types/serviceNames'
import PasswordPoliciesList from '@/views/Common/components/PasswordPoliciesList.vue'
import ResetPasswordSuccessView from '@/views/SetNewPassword/components/ResetPasswordSuccessView.vue'
import { computed, inject, ref, Ref } from 'vue'

const props = defineProps(['expired'])
const oldPassword: Ref<string> = ref('')
const newPassword: Ref<string> = ref('')
const repeatPassword: Ref<string> = ref('')
const showSuccessView: Ref<boolean> = ref(false)
const text: Ref<string> = ref('Your password has expired. Please enter a new one and confirm it.')
const config = useConfigSync()
const resetPasswordFlow = inject('resetPasswordFlow') as (newPassword: string) => Promise<void>
const updatePasswordFlow = inject('updatePasswordFlow') as (oldPassword: string, newPassword: string) => Promise<void>
const usernamePasswordLogin = inject('usernamePasswordLogin') as (username: string, password: string, selectedService: ServiceNames) => Promise<void>
const submitting: Ref<boolean> = ref(false)
const errorMessage: Ref<string> = ref('')

if (props.expired) {
  text.value = "You haven't changed your password in the last 90 days and your account is currently locked. Please fill out the following form to reset your account"
}

const formSubmitted = async () => {
  if (submitting.value || !isNewPasswordValid()) return

  submitting.value = true
  errorMessage.value = ''
  try {
    if (props.expired) {
      await updatePasswordFlow(oldPassword.value, newPassword.value)
      showSuccessView.value = true
    } else {
      await resetPasswordFlow(newPassword.value)
    }
  } catch (error: any) {
    displayError(error)
  } finally {
    submitting.value = false
  }
}

const isNewPasswordValid = () => {
  if (newPassword.value && repeatPassword.value && newPassword.value === repeatPassword.value) {
    return true
  } else {
    return false
  }
}

const onAutoLoginTriggered = async (params: any) => {
  if (submitting.value) return
  submitting.value = true
  try {
    const username = window.sessionStorage.getItem('email') || ''
    if (username === '' || username === undefined) redirect('HomeView')

    const appId: ServiceNames = window.localStorage.getItem('appId') as ServiceNames || LMS
    // After we do the autologin is done we should clean the storage to avoid having inconsistent data in any other flow.
    await usernamePasswordLogin(username, newPassword.value, appId)
    window.sessionStorage.clear()
  } catch (error: any) {
    displayError(error)
  } finally {
    submitting.value = false
  }
}

const disableSubmitButton = computed(() => !isNewPasswordValid() || submitting.value)

const isSymbolsPolicyMeet = computed((): boolean => {
  if (!config.password_policy.require_symbols) return true
  return (/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/.test(newPassword.value))
})

const isLengthPolicyMeet = computed((): boolean => {
  return newPassword.value.length >= config?.password_policy.minimum_length
})

const isLowercasePolicyMeet = computed((): boolean => {
  if (!config.password_policy.require_lowercase) return true
  return (/[a-z]/.test(newPassword.value))
})

const isRequireNumberPolicyMeet = computed((): boolean => {
  if (!config.password_policy.require_numbers) return true
  return /\d/.test(newPassword.value)
})

const isUppercasePolicyMeet = computed((): boolean => {
  if (!config.password_policy.require_uppercase) return true
  return (/[A-Z]/.test(newPassword.value))
})

const displayError = (error: any) => {
  error.message
    ? errorMessage.value = error.message
    : errorMessage.value = 'Unexpected Error'
}

</script>

<style scoped>
.form-reset-password {
  padding: 0px;
  margin: 0px;
}

.reset-password-container {
  background: transparent !important;
  padding-bottom: 120px !important;
}

.reset-password-container .card {
  width: 550px;
  box-shadow: none !important;
  background: transparent !important;
}

.reset-password-container img {
  width: 200px;
}
</style>
